import { DateTime } from "luxon";
import type { RuleT } from "../../../../../../schema/block/plot";
import { convertToAxisValue } from "./convertToAxisValue";
import { DataType } from "./dataType";
import { locale } from "../../../../../../../../../i18n/locale";
import type { PointOptionsObject } from "highcharts";

export const createAxisRulePlotLines = (
  id: string | undefined,
  type: DataType | null | undefined,
  values: RuleT[] | undefined,
  unit: string | undefined,
  color: string | undefined,
  direction: "x" | "y",
  align: "left" | "right",
): Highcharts.AxisPlotLinesOptions[] =>
  (values ?? [])
    .filter(({ value }) => value !== null && value !== undefined)
    .sort((a, b) => {
      if (typeof a.value === "number" && typeof b.value === "number") {
        return a.value - b.value;
      }
      if (typeof a.value === "string" && typeof b.value === "string") {
        return DateTime.fromISO(a.value).toMillis() - DateTime.fromISO(b.value).toMillis();
      }
      return 0;
    })
    .map(({ value }, i, arr) => ({
      value: convertToAxisValue(type, value),
      color,
      width: 1,
      dashStyle: "Dash",
      className: `th-${id}`,
      label: {
        useHTML: true,
        align: align ?? "left",
        text: `${arr.length === 2 ? (i === 0 ? "Min. " : "Max. ") : ""}${
          type === DataType.Temporal && typeof value === "string"
            ? DateTime.fromISO(value).setLocale(locale).toFormat("dd LLL yyyy")
            : value
        } ${unit === "null" ? "" : unit}`,
        y: direction === "x" ? 10 : -6,
        x: direction === "x" ? -1 : 0,
        rotation: 0,
        style: {
          color: "white",
          backgroundColor: color,
          ...(direction === "x"
            ? {
                borderTopRightRadius: "4px",
                borderBottomRightRadius: "4px",
              }
            : {
                borderTopLeftRadius: "4px",
                borderTopRightRadius: "4px",
              }),
          padding: "0.1em 0.5em",
          fontWeight: "bold",
          fontSize: "10px",
          pointerEvents: "none",
        },
      },
    }));

const THRESHOLD_PLOT_BLAND_COLOR = "rgba(237, 67, 55, 0.2)";
export function createAxisOvertakingRulePlotBands(
  serieId: string | undefined,
  data: PointOptionsObject[],
  customYRules: RuleT[] | undefined,
) {
  const overtakingValues: { className: string; from: number; to: number; color: string }[] = [];
  const yRules: RuleT[] = [];

  for (const rule of customYRules ?? []) {
    if (typeof rule.value !== "number") continue;
    yRules.push(rule);
  }

  if (yRules.length === 0) return overtakingValues;

  const className = `ov-${serieId}`;
  const maxYRule = yRules
    .filter(({ label }) => label === "max")
    .map(({ value }) => value as number);
  const minYRule = yRules
    .filter(({ label }) => label === "min")
    .map(({ value }) => value as number);

  const maxRule = maxYRule.length > 0 ? Math.max(...maxYRule) : null;
  const minRule = minYRule.length > 0 ? Math.min(...minYRule) : null;

  let fromMax;
  let fromMin;

  for (let i = 0; i < data.length; i++) {
    const point = data[i];
    if (!point || !point.x || !point.y) continue;

    if (maxRule && point.y > maxRule) {
      const nextPoint = data[i + 1];
      fromMax = fromMax ?? point.x;
      if (nextPoint?.y && nextPoint.y > maxRule) continue;

      overtakingValues.push({
        className,
        from: fromMax,
        to: point.x,
        color: THRESHOLD_PLOT_BLAND_COLOR,
      });
      fromMax = undefined;
    }

    if (minRule && point.y < minRule) {
      const nextPoint = data[i + 1];
      fromMin = fromMin ?? point.x;
      if (nextPoint?.y && nextPoint.y < minRule) continue;

      overtakingValues.push({
        className,
        from: fromMin,
        to: point.x,
        color: THRESHOLD_PLOT_BLAND_COLOR,
      });
      fromMin = undefined;
    }
  }

  return overtakingValues;
}

export function hideRulePlotLinesOrBands(serie: Highcharts.Series) {
  const linesOrBands = [
    ...(serie.yAxis?.plotLinesAndBands?.filter(value =>
      value.svgElem.hasClass(`th-${serie.userOptions.id}`),
    ) ?? []),
    ...(serie.xAxis?.plotLinesAndBands?.filter(value =>
      value.svgElem.hasClass(`ov-${serie.userOptions.id}`),
    ) ?? []),
  ];
  for (const lineOrBand of linesOrBands) {
    lineOrBand.label?.hide();
    lineOrBand.svgElem.hide();
  }
}

export function showRulePlotLinesOrBands(serie: Highcharts.Series) {
  const linesOrBands = [
    ...(serie.yAxis?.plotLinesAndBands?.filter(value =>
      value.svgElem.hasClass(`th-${serie.userOptions.id}`),
    ) ?? []),
    ...(serie.xAxis?.plotLinesAndBands?.filter(value =>
      value.svgElem.hasClass(`ov-${serie.userOptions.id}`),
    ) ?? []),
  ];
  for (const lineOrBand of linesOrBands) {
    lineOrBand.label?.show();
    lineOrBand.svgElem.show();
  }
}
